import type { AppState, ReviewRating, WordProgress } from "./types";

const STORAGE_KEY = "ars-linguae-state-v1";

const initialState: AppState = {
  locale: "fr",
  theme: "system",
  sounds: true,
  starred: [],
  progress: {},
  activityDates: [],
  enabledLists: ["essential_en", "essential_fr", "latin_phrases"],
  seenWords: [],
  studyLanguage: "all",
};

const stringArray = (value: unknown, fallback: string[] = []): string[] => Array.isArray(value) ? [...new Set(value.filter((item): item is string => typeof item === "string"))] : fallback;

export function normalizeState(value: unknown): AppState {
  const saved = value && typeof value === "object" ? value as Partial<AppState> : {};
  const enabledLists = stringArray(saved.enabledLists, initialState.enabledLists);
  const progress = saved.progress && typeof saved.progress === "object"
    ? Object.fromEntries(Object.entries(saved.progress).filter(([, item]) => item && typeof item === "object" && ["repetitions", "interval", "ease", "dueAt", "correct", "attempts"].every((key) => Number.isFinite((item as unknown as Record<string, unknown>)[key]))))
    : {};
  return {
    ...initialState,
    locale: saved.locale === "en" ? "en" : "fr",
    theme: saved.theme === "light" || saved.theme === "dark" || saved.theme === "system" ? saved.theme : initialState.theme,
    sounds: typeof saved.sounds === "boolean" ? saved.sounds : initialState.sounds,
    starred: stringArray(saved.starred),
    progress,
    activityDates: stringArray(saved.activityDates),
    enabledLists: enabledLists.length ? enabledLists : initialState.enabledLists,
    seenWords: stringArray(saved.seenWords),
    studyLanguage: saved.studyLanguage === "fr" || saved.studyLanguage === "en" || saved.studyLanguage === "la" ? saved.studyLanguage : "all",
  };
}

export function loadState(): AppState {
  try {
    return normalizeState(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? "{}"));
  } catch {
    return structuredClone(initialState);
  }
}

export function saveState(state: AppState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function schedule(progress: WordProgress | undefined, rating: ReviewRating): WordProgress {
  const previous = progress ?? { repetitions: 0, interval: 0, ease: 2.5, dueAt: 0, correct: 0, attempts: 0 };
  let repetitions = previous.repetitions;
  let interval = previous.interval;
  let ease = previous.ease;

  if (rating === "again") {
    repetitions = 0;
    interval = 1;
    ease = Math.max(1.3, ease - 0.2);
  } else if (rating === "hard") {
    repetitions = Math.max(1, repetitions);
    interval = Math.max(1, Math.round((interval || 1) * 1.2));
    ease = Math.max(1.3, ease - 0.15);
  } else if (rating === "easy") {
    repetitions += 1;
    interval = repetitions === 1 ? 4 : repetitions === 2 ? 10 : Math.max(1, Math.round(interval * ease * 1.3));
    ease = Math.min(3, ease + 0.15);
  } else {
    repetitions += 1;
    interval = repetitions === 1 ? 1 : repetitions === 2 ? 6 : Math.max(1, Math.round(interval * ease));
    ease = Math.min(2.8, ease + 0.05);
  }

  return {
    repetitions,
    interval,
    ease,
    dueAt: Date.now() + interval * 86_400_000,
    correct: previous.correct + (rating === "again" ? 0 : 1),
    attempts: previous.attempts + 1,
  };
}

export function exportState(state: AppState): void {
  const payload = JSON.stringify({ version: 1, exportedAt: new Date().toISOString(), state }, null, 2);
  const link = document.createElement("a");
  link.href = URL.createObjectURL(new Blob([payload], { type: "application/json" }));
  link.download = `ars-linguae-${new Date().toISOString().slice(0, 10)}.json`;
  link.click();
  URL.revokeObjectURL(link.href);
}

export function importState(file: File): Promise<AppState> {
  return file.text().then((text) => {
    const parsed = JSON.parse(text) as { version?: number; state?: AppState };
    if (parsed.version !== 1 || !parsed.state) throw new Error("Invalid backup");
    return normalizeState(parsed.state);
  });
}

export const todayKey = (): string => {
  const now = new Date();
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};
